"use client"

import type React from "react"
import { useState, useEffect, useCallback } from "react"
import {
  Modal,
  ModalHeader,
  ModalBody,
  ModalFooter,
  Form,
  FormGroup,
  Label,
  Input,
  FormFeedback,
  Spinner,
  Alert,
} from "reactstrap"
import { Btn } from "../../../AbstractElements"
import { RoleService } from "../../../Service/role-management-service"
import type { Role, CreateRoleRequest } from "./types"
import { toast } from "react-toastify"

interface RoleModalProps {
  isOpen: boolean
  onClose: () => void
  onSuccess: () => void
  initialData?: Role | null
}

interface FormErrors {
  name?: string
  description?: string
  general?: string
}

export default function RoleModal({ isOpen, onClose, onSuccess, initialData }: RoleModalProps) {
  const [formData, setFormData] = useState<CreateRoleRequest>({
    name: "",
    description: "",
    is_active: true,
  })
  const [errors, setErrors] = useState<FormErrors>({})
  const [loading, setLoading] = useState(false)
  const [touched, setTouched] = useState<Record<string, boolean>>({})

  const isEdit = !!initialData

  // Reset form when modal opens/closes
  useEffect(() => {
    if (isOpen) {
      if (initialData) {
        setFormData({
          name: initialData.name,
          description: initialData.description || "",
          is_active: initialData.is_active,
        })
      } else {
        setFormData({
          name: "",
          description: "",
          is_active: true,
        })
      }
      setErrors({})
      setTouched({})
    }
  }, [isOpen, initialData])

  // Handle input changes with validation
  const handleInputChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
      const { name, value, type } = e.target
      const checked = (e.target as HTMLInputElement).checked

      setFormData((prev) => ({
        ...prev,
        [name]: type === "checkbox" ? checked : value,
      }))

      // Mark field as touched
      setTouched((prev) => ({ ...prev, [name]: true }))

      // Clear error when user starts typing
      if (errors[name as keyof FormErrors]) {
        setErrors((prev) => ({ ...prev, [name]: undefined }))
      }
    },
    [errors],
  )

  // Validate form
  const validateForm = useCallback((): boolean => {
    const newErrors: FormErrors = {}

    // Name validation
    if (!formData.name.trim()) {
      newErrors.name = "Role name is required"
    } else if (formData.name.trim().length < 2) {
      newErrors.name = "Role name must be at least 2 characters"
    } else if (formData.name.trim().length > 100) {
      newErrors.name = "Role name must not exceed 100 characters"
    } else if (!/^[a-zA-Z0-9\s_-]+$/.test(formData.name.trim())) {
      newErrors.name = "Role name can only contain letters, numbers, spaces, hyphens, and underscores"
    }

    // Description validation
    if (formData.description && formData.description.length > 500) {
      newErrors.description = "Description must not exceed 500 characters"
    }

    setErrors(newErrors)
    return Object.keys(newErrors).length === 0
  }, [formData])

  // Handle form submission
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()

    if (!validateForm()) {
      return
    }

    setLoading(true)
    setErrors({})

    try {
      if (isEdit && initialData) {
        await RoleService.update(initialData.id, formData)
        toast.success("Role updated successfully")
      } else {
        await RoleService.create(formData)
        toast.success("Role created successfully")
      }
      onSuccess()
    } catch (error: any) {
      console.error("Role save error:", error)

      if (error.response?.data?.errors) {
        setErrors(error.response.data.errors)
      } else {
        setErrors({ general: error.message || "Failed to save role" })
      }
    } finally {
      setLoading(false)
    }
  }

  // Handle modal close
  const handleClose = () => {
    if (!loading) {
      onClose()
    }
  }

  return (
    <Modal isOpen={isOpen} toggle={handleClose} size="md" backdrop={loading ? "static" : true}>
      <ModalHeader toggle={handleClose}>{isEdit ? "Edit Role" : "Create New Role"}</ModalHeader>

      <Form onSubmit={handleSubmit}>
        <ModalBody>
          {errors.general && (
            <Alert color="danger" className="mb-3">
              {errors.general}
            </Alert>
          )}

          <FormGroup>
            <Label for="name">
              Role Name <span className="text-danger">*</span>
            </Label>
            <Input
              type="text"
              id="name"
              name="name"
              value={formData.name}
              onChange={handleInputChange}
              invalid={!!(touched.name && errors.name)}
              placeholder="Enter role name"
              disabled={loading}
              maxLength={100}
            />
            {touched.name && errors.name && <FormFeedback>{errors.name}</FormFeedback>}
            <div className="form-text">{formData.name.length}/100 characters</div>
          </FormGroup>

          <FormGroup>
            <Label for="description">Description</Label>
            <Input
              type="textarea"
              id="description"
              name="description"
              value={formData.description}
              onChange={handleInputChange}
              invalid={!!(touched.description && errors.description)}
              placeholder="Enter role description (optional)"
              rows={3}
              disabled={loading}
              maxLength={500}
            />
            {touched.description && errors.description && <FormFeedback>{errors.description}</FormFeedback>}
            <div className="form-text">{formData.description?.length || 0}/500 characters</div>
          </FormGroup>

          <FormGroup check>
            <Input
              type="checkbox"
              id="is_active"
              name="is_active"
              checked={formData.is_active}
              onChange={handleInputChange}
              disabled={loading}
            />
            <Label check for="is_active">
              Active Role
            </Label>
            <div className="form-text">Inactive roles cannot be assigned to users and won't appear in menus</div>
          </FormGroup>
        </ModalBody>

        <ModalFooter>
          <Btn color="secondary" onClick={handleClose} disabled={loading}>
            Cancel
          </Btn>
          <Btn color="primary" type="submit" disabled={loading}>
            {loading && <Spinner size="sm" className="me-2" />}
            {isEdit ? "Update Role" : "Create Role"}
          </Btn>
        </ModalFooter>
      </Form>
    </Modal>
  )
}
